import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";

// Define what a "site" looks like
export interface Site {
  id: string;
  name: string;
  domain?: string;
  theme: string;
  integrations: string[];
  [key: string]: any; // allow extra fields
}

interface SiteContextType {
  selectedSite: Site | null;
  setSelectedSite: (site: Site) => void;
  clearSite: () => void;
}

const SiteContext = createContext<SiteContextType | undefined>(undefined);

export const SiteProvider = ({ children }: { children: ReactNode }) => {
  const [selectedSite, setSelectedSiteState] = useState<Site | null>(null);

  // Load from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem("selectedSite");
    if (stored) {
      try {
        setSelectedSiteState(JSON.parse(stored));
      } catch {
        localStorage.removeItem("selectedSite"); // fallback if corrupted
      }
    }
  }, []);

  const setSelectedSite = (site: Site) => {
    localStorage.setItem("selectedSite", JSON.stringify(site));
    setSelectedSiteState(site);
  };

  const clearSite = () => {
    localStorage.removeItem("selectedSite");
    setSelectedSiteState(null);
  };

  return (
    <SiteContext.Provider value={{ selectedSite, setSelectedSite, clearSite }}>
      {children}
    </SiteContext.Provider>
  );
};

export const useSite = (): SiteContextType => {
  const ctx = useContext(SiteContext);
  if (!ctx) {
    throw new Error("useSite must be used inside a <SiteProvider>");
  }
  return ctx;
};
